import React, { useRef } from 'react'
import useSize from './useSize'
import { scaleLinear } from 'd3-scale'
import { line, curveBasis } from 'd3-shape'
import {
  HorizontalTicks,
  VerticalTicks,
  AxisLabel,
  Curve
} from 'components/ChartElements'
import './styles.scss'

const f = new Intl.NumberFormat('de-CH')

type ChartProps = {
  curves: [number, number][][]
  keys: string[][]
  axisLabels?: { x?: string; y?: string }

  xDomain?: number[]
  yDomain?: number[]
  yTickStep?: number
}
export const Chart: React.FC<ChartProps> = ({
  curves,
  keys,
  axisLabels,
  xDomain = [1925, 2020],
  yDomain = [0, 1],
  yTickStep = 1
}) => {
  const yFunction = (d: number[]) => yAxisScale(d[1])
  const xFunction = (d: number[]) => xAxisScale(d[0])

  const [container, size] = useSize()
  const svg = useRef<SVGSVGElement>(null)
  //
  const width = size.width
  const height = 350
  const gutterTop = axisLabels && axisLabels.y ? 18 : 0
  const gutterRight = 2
  const gutterLeft = 0
  const gutterBottom = 25
  const plotArea = {
    top: gutterTop,
    right: width - gutterRight,
    left: gutterLeft,
    bottom: height - gutterBottom
  }

  const xAxisScale = React.useMemo(
    () =>
      scaleLinear()
        .domain(xDomain)
        .range([plotArea.left, plotArea.right]),
    [size]
  )

  const yAxisScale = React.useMemo(
    () =>
      scaleLinear()
        .domain(yDomain)
        .range([plotArea.bottom, plotArea.top]),
    [size]
  )

  const lineFunction = React.useMemo(
    () =>
      line()
        .x(xFunction)
        .y(yFunction)
        .curve(curveBasis),
    [size]
  )

  let yTicks = new Array(Math.ceil((yDomain[1] - yDomain[0] + 1) / yTickStep))
    .fill(0)
    .map((e, i) => yDomain[0] + i * yTickStep)

  let xTicks = [1950, 1975, 2000, 2020]

  return (
    <div ref={container} style={{ position: 'relative', width: '100%' }}>
      <svg
        ref={svg}
        width={width}
        height={height}
        style={{ backgroundColor: 'transparent' }}
      >
        {axisLabels && axisLabels.y && (
          <AxisLabel text={axisLabels.y} dir='up' pos={[0, 0]} />
        )}
        {window.innerWidth > 600 && axisLabels && axisLabels.x && (
          <AxisLabel
            text={axisLabels.x}
            dir='right'
            pos={[0, yAxisScale.range()[0] + 10]}
          />
        )}
        <rect
          x={gutterLeft}
          y={gutterTop}
          width={size.width - gutterLeft - gutterRight}
          height={size.height - gutterTop - gutterBottom - 10}
          fill={'#474A6644'}
        />

        <VerticalTicks
          values={xTicks}
          x={xAxisScale}
          y={yAxisScale}
          format={(m: number) => {
            return m.toString()
          }}
          textAnchor={element => {
            if (element === xTicks[xTicks.length - 1]) return 'end'
            return 'middle'
          }}
        />
        <HorizontalTicks
          values={yTicks}
          x={xAxisScale}
          y={yAxisScale}
          format={(m: number) => {
            return m.toFixed(countDecimals(yTickStep))
          }}
          textAnchor={'start'}
        />
        {curves.map((m, i) => {
          return (
            <>
              <Curve
                key={i}
                d={lineFunction(m) || ''}
                color={keys[i][1]}
                className={'_' + (i + 1)}
              />
              {/* <rect x={x-width/2} y={y} width={width} height={height} fill={keys[i][1]}/> */}
            </>
          )
        })}
      </svg>
    </div>
  )
}

const countDecimals = (value: number) => {
  if (Math.floor(value) !== value)
    return value.toString().split('.')[1].length || 0
  return 0
}
